import { Injectable } from '@angular/core';
import { ResourceType } from '@app/core/model/resource-type';
import { BsApiService } from '@app/core/services/bs-api/bs-api-service';
import { PageContextService } from '@app/core/services/page-context.service';
import { ArrayUtilities } from '@app/core/utilities/array-utilities';
import { BehaviorSubject, Observable} from 'rxjs';
import { delay, finalize, map, retryWhen, shareReplay, tap } from 'rxjs/operators';
import { genericRetryWhen, observableEmitDelay } from '../../rxjsutils/rxjs-utilities';
import { AbstractResourceStore } from '../resource/abstract.resource.store';
import { IResourceStore } from '../resource/resource.store';
import { IPoolsState} from './pools.state';
import { isNullOrUndefined } from 'util';
import { IPool } from '@app/core/model/pool/pool';
import { ILibrary } from '@app/core/model/library/library';
import _ from 'lodash';
import { IBioSample } from '@app/core/model/bioSamples/bioSample';
import { RequeueService } from '@app/core/services/requeue/requeue.service';
import { ICapability } from '@app/core/model/Capability/Capability';
import { BasespaceService, V2CreateExistingPoolLabRequeueRequest, V2LabRequeue } from '@bssh/ng-sdk';
import { ToastrMessages } from '@app/core/utilities/toastr-messages';
import { ToastrService } from '@bssh/comp-lib';

export interface IPoolsStore extends IResourceStore<IPoolsState> {
    loadCurrentPool(id: string, forceLoad: boolean): void;
}

// The action-names for this store, for logging/tracking.
export enum PoolsStoreActions {
    LoadPoolDetails = 'LOAD_POOL_DETAILS',
    LoadPoolLibraries = 'LOAD_POOL_LIBRARIES',
    UpdateSelectedPools = 'UPDATE_SELECTED_POOLS',
    UpdateSelectedLibraries = 'UPDATE_SELECTED_LIBRARIES',
    UpdateSelectedBioSamples = 'UPDATE_SELECTED_BIOSAMPLES'
}
export interface IPoolCapabilities {
  LabRequeue: ICapability;
}

@Injectable({
    providedIn: 'root'
})
export class PoolsStore extends AbstractResourceStore<IPoolsState> implements IPoolsStore {

  private refreshSubject = new BehaviorSubject<boolean>(false);
  poolsRefreshed$ = this.refreshSubject.asObservable();

  constructor(
    private bsApiService: BsApiService,
    private requeueService: RequeueService,
    private basespaceService: BasespaceService,
    private toastrService: ToastrService
  ) {
      super(
        [
          'currentPool',
          'poolStateError',
          'selectedPools',
          'selectedLibraries',
          'selectedBioSamples',
          'currentUserId'
        ]
      );
  }

  // method to access @refreshSubject externally to refresh current view
  forceRefreshStore() {
    this.refreshSubject.next(true);
  }

  /**
   * Calls BSSH API to load sample details and cache in the  store
   * @param id Id of the sample to fetch. Will not fetch if the state's currentSample already matches this id.
   */
  loadCurrentPool(id: string, forceLoad: boolean = false) {

    const currentState = this.getState();
    if (currentState && 
      currentState.currentPool && 
      currentState.currentPool.Id === id && 
      !forceLoad) 
    {
        // if pool already set don't fetch, but dispatch current state if another call is made later
        this.dispatchCurrentState(currentState);
    } else 
    {
      this.loadingSubject.next(true);

      // Subscribe to retrieve
      this.subs.sink = this.bsApiService.getPool(
        id).pipe(
          // Do not make an additional API call if any additional subscribers have subscribed later on
          shareReplay(1),
          // Retry in case of HTTP errors
          retryWhen(genericRetryWhen()),
          // To avoid a Flash of content, maintain a delay
          delay(observableEmitDelay),
          // Stop loading in finalize.
          finalize(() => this.loadingSubject.next(false))
        ).subscribe({
          next: (results) => {
              this.setState({ currentPool: results}, PoolsStoreActions.LoadPoolDetails);
          },
          error: error => this.handleError(error, () => ({ ...currentState, poolStateError: error }))
        });
    }
  }

  /**
   * Unload current pool data to avoid stale data displayed when enter another pool details page
   */
  unloadCurrentPool() {
    this.setState({ currentPool: undefined });
  }

  updateSelectedPools(updatedSelections: IPool[]) {
    this.setState({ selectedPools: updatedSelections }, PoolsStoreActions.UpdateSelectedPools);
  }

  updateSelectedLibraries(updatedSelections: ILibrary[]) {
    this.setState({ selectedLibraries: updatedSelections }, PoolsStoreActions.UpdateSelectedLibraries);
  }

  updateSelectedBioSamples(updatedSelections: IBioSample[]) {
    this.setState({ selectedBioSamples: updatedSelections }, PoolsStoreActions.UpdateSelectedBioSamples);
  }

  getActiveList(): IPool[] {
    const { currentPool } = this.getState();
   
    if(!isNullOrUndefined(currentPool)) {
      return [currentPool];
    } else {
      return null;
    }
  }

  canTrash(): boolean {
    return false;
  }

  canEditDefaultProjectInfo(): boolean {
    const { currentPool, selectedPools, selectedLibraries, selectedBioSamples } = this.getState();

    if (ArrayUtilities.isNullOrEmpty(selectedLibraries) && ArrayUtilities.isNullOrEmpty(selectedBioSamples)) {
      return false;
    }

    return selectedBioSamples.every(biosample => this.isResourceOwner(biosample));
  }

  canEditBioSampleInfo(): boolean {
    const { currentPool, selectedPools, selectedLibraries } = this.getState();
    if (!ArrayUtilities.isArrayOfSize(selectedLibraries, 1)) {
      return;
    }
    return this.isResourceOwner(selectedLibraries[0].BioSample);
  }

  canRequeueLibrary(): boolean {
    const activePools = this.getActiveList();
    // only allowed for single selection
    if (!ArrayUtilities.isArrayOfSize(activePools, 1)) {
        return false;
    }
    const { currentUserId } = this.getState();
    return this.requeueService.canRequeuePool(activePools[0], currentUserId);
  }

   getLibraryPoolCapabilities(poolId: string): Observable<IPoolCapabilities> {
      return this.bsApiService.getPoolCapabilities(poolId).pipe(
        map((response) => {
          console.log(response);
          return response
        }));
  }

  requeuePool(requestedAdditionalYield: number): void {
    this.loadingSubject.next(true);
    const activePools = this.getActiveList();

    if (!ArrayUtilities.isArrayOfSize(activePools, 1)) {
        return;
    }

    const payload: V2CreateExistingPoolLabRequeueRequest = {
        RequestedAdditionalYield: requestedAdditionalYield,
        LIMSId: null,
    };

    const params: BasespaceService.PostV2LibrarypoolsPoolidLabrequeuesParams = {
      poolid: activePools[0].Id.toString(), 
      payload
    };

    this.subs.sink = this.basespaceService.PostV2LibrarypoolsPoolidLabrequeues(params).pipe(
        // Retry in case of HTTP errors
        retryWhen(genericRetryWhen()),
        // To avoid a Flash of content, maintain a delay
        delay(observableEmitDelay),
        finalize(() => this.loadingSubject.next(false))
    ).subscribe({
        next: (apiResponses: V2LabRequeue) => {
            this.setState({ poolStateError: null });
            this.refreshSubject.next(true);
            this.toastrService.success(ToastrMessages.LAB_REQUEUE_REQUESTED);
        },
        error: error => {
            this.handleError(error, () => ({ ...this.getState(), poolStateError: error }));
        }
    });
  }

  getResourceType(): ResourceType {
    return ResourceType.POOLS;
  }
}